import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Building2, Users, ChevronRight } from 'lucide-react';

const Admin = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    clientName: '',
    clientCode: '',
    adminEmail: '',
    adminPassword: '',
    adminName: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/admin/clients');
      setClients(response.data.clients || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);

    try {
      // Validate client code (alphanumeric and underscore only)
      if (!/^[a-zA-Z0-9_]+$/.test(formData.clientCode)) {
        setError('Client code must be alphanumeric (letters, numbers, underscore only)');
        setCreating(false);
        return;
      }

      const response = await api.post('/admin/clients', {
        clientName: formData.clientName,
        clientCode: formData.clientCode.toLowerCase(),
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword,
        adminName: formData.adminName
      });

      setSuccess(`Client "${response.data.client.client_name}" created successfully!`);
      setFormData({
        clientName: '',
        clientCode: '',
        adminEmail: '',
        adminPassword: '',
        adminName: ''
      });
      fetchClients();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowModal(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Client Administration</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Add Client
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-label">Total Clients</div>
          <div className="stat-value">{clients.filter(c => c.clientCode !== 'system').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Clients</div>
          <div className="stat-value success">
            {clients.filter(c => c.isActive && c.clientCode !== 'system').length}
          </div>
        </div>
      </div>

      {/* Clients List */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Clients</h2>
        </div>
        {clients.filter(c => c.clientCode !== 'system').length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p>No clients yet</p>
            <p className="text-sm text-muted">Create your first client to get started</p>
          </div>
        ) : (
          <div>
            {clients.filter(c => c.clientCode !== 'system').map((client) => (
              <div
                key={client.clientId}
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div className="flex items-center gap-4">
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: 'var(--radius)',
                    background: 'var(--primary)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600'
                  }}>
                    {(client.clientName || client.clientCode || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{client.clientName || client.clientCode}</div>
                    <div className="text-sm text-muted">
                      Code: {client.clientCode} • Schema: client_{client.clientCode}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted">
                    <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    {client.userCount || 0} users
                  </div>
                  <span className={`badge badge-${client.isActive ? 'success' : 'secondary'}`}>
                    {client.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Client Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px' }}>
            <div className="card-header">
              <h3 className="card-title">Create New Client</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateClient}>
              <div className="card-body">
                {error && <div className="alert alert-error mb-4">{error}</div>}
                {success && <div className="alert alert-success mb-4">{success}</div>}

                <div style={{ 
                  background: 'var(--bg-secondary)', 
                  padding: '12px', 
                  borderRadius: 'var(--radius)',
                  marginBottom: '16px'
                }}>
                  <div className="text-sm font-medium mb-1">Client Details</div>
                  <div className="text-sm text-muted">
                    This creates a new tenant with its own database schema
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Client Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.clientName}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                    placeholder="e.g. Gexpro Services"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Client Code *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.clientCode}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      clientCode: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                    }))}
                    placeholder="e.g. gexpro"
                    required
                  />
                  <div className="text-sm text-muted" style={{ marginTop: '4px' }}>
                    Lowercase, alphanumeric only. Schema will be: client_{formData.clientCode || 'code'}
                  </div>
                </div>

                <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

                <div style={{ 
                  background: 'var(--bg-secondary)', 
                  padding: '12px', 
                  borderRadius: 'var(--radius)',
                  marginBottom: '16px'
                }}>
                  <div className="text-sm font-medium mb-1">Client Admin User</div>
                  <div className="text-sm text-muted">
                    This user will have full admin access to the client
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Admin Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.adminName}
                    onChange={(e) => setFormData(prev => ({ ...prev, adminName: e.target.value }))}
                    placeholder="e.g. John Smith"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Admin Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, adminEmail: e.target.value }))}
                    placeholder="e.g. admin@gexpro.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Admin Password *</label>
                  <input
                    type="password"
                    className="form-input"
                    value={formData.adminPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, adminPassword: e.target.value }))}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div style={{ 
                padding: '16px 20px', 
                borderTop: '1px solid var(--border)', 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px' 
              }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
